import { Auth } from '@aws-amplify/auth'

/**
 * Helper function to make authenticated API requests
 */
const apiRequest = async (endpoint, method = 'GET', body = null) => {
  try {
    const user = await Auth.currentAuthenticatedUser()
    const token = user.signInUserSession.idToken.jwtToken
    const baseUrl = import.meta.env.VITE_API_URL
    
    const options = {
      method,
      headers: {
        'Authorization': token,
        'Content-Type': 'application/json'
      }
    }
    
    if (body) {
      options.body = JSON.stringify(body)
    }
    
    const response = await fetch(`${baseUrl}${endpoint}`, options)
    
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`API error: ${response.status} - ${errorText}`)
    }
    
    return response.json()
  } catch (error) {
    console.error('API request error:', error)
    throw error
  }
}

/**
 * Check if the current user has completed signup
 * @returns {Promise<Object>} Object with signupComplete status and user data if available
 */
export const checkSignupStatus = async () => {
  try {
    return await apiRequest('/signup/check')
  } catch (error) {
    console.error('Error checking signup status:', error)
    // Return a valid response structure with signupComplete false
    return { signupComplete: false }
  }
}

/**
 * Submit user signup information
 * 
 * @param {Object} userData User data for signup
 * @param {string} userData.firstName User's first name
 * @param {string} userData.lastName User's last name
 * @param {string} userData.email User's email address
 * @param {string} userData.college User's college or university
 * @param {string} [userData.linkedin] Optional LinkedIn profile URL
 * @returns {Promise<Object>} API response with success message and user data
 */
export const submitSignup = async (userData) => {
  // Make sure email is included and not empty
  if (!userData.email || userData.email.trim() === '') {
    console.error('Email is required for signup')
    throw new Error('Email is required for signup')
  }
  
  try {
    return await apiRequest('/signup', 'POST', userData)
  } catch (error) {
    console.error('Error submitting signup:', error)
    throw error
  }
}

export default {
  checkSignupStatus,
  submitSignup
}; 